import React from 'react';
import {Select} from '@mantine/core';
import {Stack} from 'src/design/atoms/Stack';
import type {
  WorkflowDefinitionTriggerResponse} from 'src/api/ProactApiClient';
import {
  FormStateEnum
} from 'src/api/ProactApiClient';
import {useGetFormDefinitionSelectOptionsQuery} from 'src/redux/queries/formDefinition';
import useSkipTokensByFeature from 'src/shared/hooks/useSkipTokensByFeature';
import type {TriggerEditorProps} from './TriggerCard';

const formStateOptions = [
  {value: '', label: '(Any status)'},
  {value: String(FormStateEnum.Published), label: 'Published'},
  {value: String(FormStateEnum.Failed), label: 'Failed'},
];

export const TriggerFormSubmissionEditor: React.FC<TriggerEditorProps> = ({
  trigger,
  onTriggerChange,
}) => {
  const {skipForms} = useSkipTokensByFeature();

  const {data: forms, isLoading: isFormsLoading} =
    useGetFormDefinitionSelectOptionsQuery(
      {start: 0, length: 10000, includeSubforms: true},
      {skip: skipForms}
    );

  const formOptions =
    forms?.data?.map(f => ({
      value: String(f.id),
      label: f.label ?? String(f.id),
    })) ?? [];

  const handleFormChange = (value: string | null) => {
    onTriggerChange({
      ...trigger,
      triggerFormDefinitionId: value ? Number(value) : undefined,
    } as WorkflowDefinitionTriggerResponse);
  };

  const handleFormStateChange = (value: string | null) => {
    onTriggerChange({
      ...trigger,
      triggerFormState:
        value === '' || value === null
          ? undefined
          : (Number(value) as FormStateEnum),
    } as WorkflowDefinitionTriggerResponse);
  };

  return (
    <Stack gap="md">
      <Select
        label="Form"
        description="The workflow will trigger when this form is submitted."
        data={formOptions}
        value={
          trigger.triggerFormDefinitionId
            ? String(trigger.triggerFormDefinitionId)
            : null
        }
        onChange={handleFormChange}
        disabled={isFormsLoading}
        searchable
        required
      />
      <Select
        label="Form status filter"
        description="If left blank, the workflow will trigger when the form is completed with any status."
        data={formStateOptions}
        value={
          trigger.triggerFormState != null
            ? String(trigger.triggerFormState)
            : ''
        }
        onChange={handleFormStateChange}
        disabled={isFormsLoading}
      />
    </Stack>
  );
};
